import { usePageContent } from "@/hooks/usePageContent";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { useSettings } from "@/context/SettingsContext";

export default function ContactSection() {
  const { getContent } = usePageContent('homepage');
  const { settings } = useSettings();
  const displayPhone = settings.general.primary_phone || '+91 90350 63455';
  const phoneRaw = settings.general.primary_phone_raw || '+919035063455';
  const contactEmail = settings.general.contact_email || 'support@cashmyproperty.ai';
  const ctaHeading = getContent('home-contact-heading') || 'Ready to Unlock Your Land\'s Potential?';
  const { status, errorMsg, charCount, setCharCount, submit } = useFormSubmit({
    formType: "contact",
  });

  return (
    <section id="contact" className="py-24 bg-background-100">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
          {/* Left */}
          <div>
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-4">
              Get Started
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              {ctaHeading}
            </h2>
            <p className="text-foreground-600 mb-8 leading-relaxed">
              Don&apos;t let your biggest asset sleep. Get a free preliminary assessment today. Our AI
              will analyze your land and one of our strategists will call you within 24 hours.
            </p>

            <div className="space-y-4 mb-8">
              <a
                href={`tel:${phoneRaw}`}
                className="flex items-center gap-3 text-foreground-600 hover:text-primary-500 transition-colors"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-primary-500/30 shrink-0">
                  <i className="ri-phone-line text-primary-500" />
                </div>
                {displayPhone}
              </a>
              <a
                href={`mailto:${contactEmail}`}
                className="flex items-center gap-3 text-foreground-600 hover:text-primary-500 transition-colors"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-primary-500/30 shrink-0">
                  <i className="ri-mail-line text-primary-500" />
                </div>
                {contactEmail}
              </a>
            </div>

            {/* Testimonials snippet */}
            <div className="p-5 rounded-xl card-dark">
              <div className="flex items-center gap-1 mb-2">
                {[1,2,3,4,5].map(i => (
                  <i key={i} className="ri-star-fill text-accent-500 text-sm" />
                ))}
              </div>
              <p className="text-sm text-foreground-600 italic leading-relaxed">
                &ldquo;CashMyProperty.ai unlocked ₹2.5 Lakh monthly income from my highway plot that was
                lying idle for 12 years. The team handled everything — I didn&apos;t have to lift a finger.&rdquo;
              </p>
              <div className="mt-3 text-sm text-foreground-500 font-medium">
                — Rajesh S., NRI Landowner, Bengaluru
              </div>
            </div>
          </div>

          {/* Form */}
          <div>
            <form onSubmit={submit} className="p-8 rounded-2xl card-dark space-y-5">
              <h3 className="font-display font-semibold text-lg text-foreground-950 mb-2">
                Get Your Feasible AI Report
              </h3>

              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 w-0 h-0 pointer-events-none"
              />

              <input
                name="name"
                placeholder="Full Name *"
                required
                className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
              />
              <input
                name="email"
                type="email"
                placeholder="Email Address *"
                required
                className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
              />
              <input
                name="phone"
                type="tel"
                placeholder="Phone Number *"
                required
                className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
              />
              <input
                name="location"
                placeholder="Land Location (City / Area) *"
                required
                className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
              />
              <select
                name="landSize"
                className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
              >
                <option value="">Land Size (Approx)</option>
                <option value="small">Less than 5,000 sq.ft</option>
                <option value="medium">5,000 – 10,000 sq.ft</option>
                <option value="large">10,000 sq.ft – 1 Acre</option>
                <option value="xlarge">1 Acre+</option>
              </select>
              <div className="relative">
                <textarea
                  name="message"
                  placeholder="Tell us about your land (optional)"
                  rows={3}
                  maxLength={500}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50 resize-none"
                />
                <span className={`absolute bottom-2 right-3 text-xs ${charCount > 450 ? "text-accent-500" : "text-foreground-500"}`}>
                  {charCount}/500
                </span>
              </div>

              {/* Hidden source */}
              <input type="hidden" name="source" value="Homepage" />

              {status === "success" && (
                <div className="p-4 rounded-xl bg-primary-500/10 border border-primary-700/30 text-primary-600 text-sm text-center">
                  <i className="ri-checkbox-circle-line mr-2" />
                  Thank you! We&apos;ll call you within 24 hours.
                </div>
              )}
              {status === "error" && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-800/30 text-red-400 text-sm text-center">
                  <i className="ri-error-warning-line mr-2" />
                  {errorMsg || "Something went wrong. Please try again or call us directly."}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {status === "loading" ? "Submitting..." : "Get My AI Report Now"}
              </button>
              <p className="text-xs text-foreground-500 text-center">
                No spam. 100% confidential. We never share your data.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}